import { logger } from '@breathelife/monitoring-frontend';
import {
  DEFAULT_TIMEZONE_NAME,
  InsuranceModule,
  InsuranceModules,
  Localized,
  NodeIdToAnswerPathMap,
  PivotAnswerResolver,
  QuestionnaireBlueprint,
  QuestionnaireDefinition,
  QuestionnaireEngine,
  Timezone,
  VersionedAnswers,
} from '@zinnia/form-engine-sdk';

import { Participant } from '@breathelife/types';
import { getQuestionnaireEngineConfig } from '../../Helpers/questionnaireEngineConfigs';
import { Application } from '../../Models/Application';
import { PlatformFeatures } from '../../Models/PlatformFeatures';
import { CarrierQuestionnaireResponse } from '../../Services/CarrierQuestionnaireService';

export type ApplicationProgressProps = {
  application?: Application;
  needsAnalysisOnly?: boolean;
  questionnaireResponse?: CarrierQuestionnaireResponse;
};

type ApplicationProgressByModuleProps = {
  application?: Application;
  insuranceModule: InsuranceModule;
  nodeIdToAnswerPathMap?: NodeIdToAnswerPathMap;
  questionnaire?: Localized<QuestionnaireDefinition>;
  blueprint?: QuestionnaireBlueprint;
  features: PlatformFeatures;
  participants?: Participant[];
  isAuraNextEnabled?: boolean;
};

export function getApplicationProgressByModule(props: ApplicationProgressByModuleProps): number {
  const {
    application,
    insuranceModule,
    nodeIdToAnswerPathMap,
    questionnaire,
    blueprint,
    features,
    participants,
    isAuraNextEnabled,
  } = props;

  if (!application || !nodeIdToAnswerPathMap || !questionnaire || !blueprint) return 0;

  const timezoneResult = Timezone.from(application.timezone || DEFAULT_TIMEZONE_NAME);
  if (timezoneResult.success === false) {
    return 0;
  }

  const answerResolver = PivotAnswerResolver.from(
    new VersionedAnswers({ v1: application.answers, v2: application.answersV2 }),
    nodeIdToAnswerPathMap,
    blueprint,
    'v1',
    logger,
  );

  const questionnaireEngine = QuestionnaireEngine.from({
    blueprint,
    questionnaire,
    answerResolver,
    context: {
      insuranceModules: [insuranceModule],
    },
    config: getQuestionnaireEngineConfig(application.mode, features),
    timezone: timezoneResult.value,
    applicationContext: application.applicationContext,
    language: application.lang,
    currentDateOverride: application.submissionDate?.toString() || null,
    cloningStrategy: 'Fast',
  });

  if (!questionnaireEngine) return 0;

  const isCompleted =
    insuranceModule === InsuranceModules.insuranceApplication ? application.signed || application.submitted : undefined;

  let removedPercentage = 0;
  if (isAuraNextEnabled && participants?.length) {
    const nonCompletedParticipantsCount = participants.filter(
      (participant) =>
        participant.medicalInterview?.status !== 'COMPLETED' && participant.medicalInterview?.status !== 'SKIPPED',
    ).length;

    removedPercentage = (nonCompletedParticipantsCount / participants.length) * 10;
  }

  return Math.round(questionnaireEngine.calculateProgress(isCompleted) - removedPercentage);
}
